/**
 * Custom Foundation wizard: Capture -> Your Match -> Customize.
 *
 * Image analysis is NOT something this theme can do itself — it's a plain
 * browser script, there's no server runtime here. The integration surface is
 * split into three named pieces so a future backend/app can be dropped in
 * without touching the wizard UI:
 *
 *   AnalysisService     — talks to the external shade-analysis backend (or
 *                          falls back to a local sample table). Owns the
 *                          {apiUrl}/analyze HTTP contract.
 *   RecommendationEngine — turns a raw analysis into a customer-facing shade
 *                          recommendation (name, swatch, formula label).
 *   ShadeFinderProvider  — orchestrates the two above; this is the only
 *                          object the wizard UI (init(), below) calls.
 *
 * ShadeResult (what ShadeFinderProvider.analyze() resolves to) matches the
 * contract this theme expects a real analysis backend to return:
 *   { skinTone, undertone, depth, lightingQuality, confidenceScore,
 *     recommendedFormula, shade, hex, isSample }
 * skinTone/lightingQuality are passed through only when a real API supplies
 * them — the local sample fallback leaves them blank rather than inventing
 * values. Verify/adjust AnalysisService.analyzeRemote's request/response
 * shape against the real backend before relying on it in production.
 */
(function () {
  var SHADE_TABLE = {
    'Fair-Cool': { shade: 'Porcelain Cool 1', hex: '#F3DCCB', undertone: 'Cool' },
    'Fair-Neutral': { shade: 'Porcelain Neutral 1', hex: '#F1D9C5', undertone: 'Neutral' },
    'Fair-Warm': { shade: 'Porcelain Warm 1', hex: '#F0D5BC', undertone: 'Warm' },
    'Light-Cool': { shade: 'Ivory Cool 2', hex: '#E9C7AE', undertone: 'Cool' },
    'Light-Neutral': { shade: 'Ivory Neutral 2', hex: '#E6C2A6', undertone: 'Neutral' },
    'Light-Warm': { shade: 'Ivory Warm 2', hex: '#E3BC9C', undertone: 'Warm' },
    'Medium-Cool': { shade: 'Honey Cool 3', hex: '#C99B72', undertone: 'Cool' },
    'Medium-Neutral': { shade: 'Honey Neutral 3', hex: '#C6946A', undertone: 'Neutral' },
    'Medium-Warm': { shade: 'Honey Warm 3', hex: '#C38C5F', undertone: 'Warm' },
    'Tan-Cool': { shade: 'Amber Cool 4', hex: '#A66A41', undertone: 'Cool' },
    'Tan-Neutral': { shade: 'Amber Neutral 4', hex: '#A16238', undertone: 'Neutral' },
    'Tan-Warm': { shade: 'Amber Warm 4', hex: '#9C592E', undertone: 'Warm' },
    'Deep-Cool': { shade: 'Espresso Cool 5', hex: '#6B4128', undertone: 'Cool' },
    'Deep-Neutral': { shade: 'Espresso Neutral 5', hex: '#623A22', undertone: 'Neutral' },
    'Deep-Warm': { shade: 'Espresso Warm 5', hex: '#59331C', undertone: 'Warm' },
  };

  /**
   * The real Bloom shade catalog ("I know my shade" code entry) — see
   * apps/backend/app/pipeline/shade_matching.py's SHADE_CATALOG, the
   * source of truth this mirrors. Code = depth hundreds-digit (100
   * very_light .. 600 dark) + undertone tens-digit offset (0 cool, 10
   * neutral, 20 warm). Depth label collapses to the same three
   * customer-facing categories (Light/Medium/Dark) used everywhere else
   * in the app now — see the Next.js app's lib/copy.ts humanizeDepth,
   * customers should never see the finer six-tier internal names.
   */
  var SHADE_CODE_DEPTH_LABEL = {
    100: 'Light', 200: 'Light',
    300: 'Medium', 400: 'Medium',
    500: 'Dark', 600: 'Dark',
  };
  var SHADE_CODE_UNDERTONE_LABEL = { 0: 'Cool', 10: 'Neutral', 20: 'Warm' };
  var VALID_SHADE_CODES = Object.keys(SHADE_CODE_DEPTH_LABEL).reduce(function (codes, hundreds) {
    Object.keys(SHADE_CODE_UNDERTONE_LABEL).forEach(function (offset) {
      codes.push(Number(hundreds) + Number(offset));
    });
    return codes;
  }, []);

  /** Parses free-text like "510", "Bloom 510", "bloom510" into a numeric
   * code, or null if it isn't a plausible one — used by the "I know my
   * shade" input, not by anything reading a real analysis result. */
  function parseShadeCode(raw) {
    if (!raw) return null;
    var digits = String(raw).replace(/[^0-9]/g, '');
    if (!digits) return null;
    return Number(digits);
  }

  function lookupShadeCode(raw) {
    var code = parseShadeCode(raw);
    if (code == null || VALID_SHADE_CODES.indexOf(code) === -1) return null;
    var hundreds = Math.floor(code / 100) * 100;
    var offset = code - hundreds;
    return {
      shade: 'Bloom ' + code,
      depth: SHADE_CODE_DEPTH_LABEL[hundreds],
      undertone: SHADE_CODE_UNDERTONE_LABEL[offset],
    };
  }

  var DEPTH_ALIASES = {
    fair: 'Fair', porcelain: 'Fair',
    light: 'Light', ivory: 'Light',
    medium: 'Medium', natural: 'Medium',
    tan: 'Tan', amber: 'Tan',
    dark: 'Deep', deep: 'Deep', espresso: 'Deep',
  };

  var UNDERTONE_ALIASES = {
    cool: 'Cool', neutral: 'Neutral', warm: 'Warm',
  };

  /**
   * Bloom's own AI tool doesn't send back a real swatch color (hex) — only
   * depth + undertone labels. Rather than show the same placeholder color
   * for every match, approximate a real-looking swatch from those labels
   * using the same depth/undertone spectrum as the local sample table.
   */
  function approximateHex(depth, undertone) {
    var depthKey = DEPTH_ALIASES[(depth || '').toLowerCase()] || 'Medium';
    var undertoneKey = UNDERTONE_ALIASES[(undertone || '').toLowerCase()] || 'Neutral';
    var match = SHADE_TABLE[depthKey + '-' + undertoneKey] || SHADE_TABLE['Medium-Neutral'];
    return match.hex;
  }

  function generateAnalysisId() {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
    return 'cf-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
  }

  /** Owns the {apiUrl}/analyze HTTP contract and the local sample fallback. */
  var AnalysisService = {
    /** Deterministic offline fallback — never presented as a real AI result (flagged isSample: true). */
    localSample: function (quiz) {
      var depth = (quiz && quiz.depth) || 'Medium';
      var undertone = (quiz && quiz.undertone) || 'Neutral';
      var match = SHADE_TABLE[depth + '-' + undertone] || SHADE_TABLE['Medium-Neutral'];
      return Promise.resolve({
        shade: match.shade,
        hex: match.hex,
        undertone: match.undertone,
        depth: depth,
        skinTone: null,
        lightingQuality: null,
        confidenceScore: quiz ? 92 : 85,
        isSample: true,
      });
    },

    /** POSTs the selfie to {apiUrl}/analyze. Placeholder contract — verify against the real backend. */
    analyzeRemote: function (apiUrl, file) {
      var body = new FormData();
      body.append('image', file);
      return fetch(apiUrl.replace(/\/$/, '') + '/analyze', { method: 'POST', body: body })
        .then(function (res) {
          if (!res.ok) throw new Error('Shade analysis request failed');
          return res.json();
        })
        .then(function (data) {
          if (!data || !data.shade || !data.hex || !data.undertone) {
            throw new Error('Unexpected shade analysis response shape');
          }
          return {
            shade: data.shade,
            hex: data.hex,
            undertone: data.undertone,
            depth: data.depth || null,
            skinTone: data.skinTone || null,
            lightingQuality: data.lightingQuality || null,
            confidenceScore: data.confidenceScore || data.confidence || 90,
            isSample: false,
          };
        });
    },
  };

  /** Turns a raw analysis into the customer-facing recommendation. */
  var RecommendationEngine = {
    recommend: function (analysis) {
      return Object.assign({}, analysis, {
        recommendedFormula: analysis.shade,
        analysisId: generateAnalysisId(),
      });
    },
  };

  /** The only object the wizard UI talks to. */
  var ShadeFinderProvider = {
    analyze: function (options) {
      var apiUrl = options.apiUrl;
      var photo = options.photo;
      var quiz = options.quiz;

      var analysisPromise;
      if (photo && apiUrl) {
        analysisPromise = AnalysisService.analyzeRemote(apiUrl, photo).catch(function () {
          return AnalysisService.localSample(quiz);
        });
      } else {
        analysisPromise = new Promise(function (resolve) {
          setTimeout(function () {
            resolve(AnalysisService.localSample(quiz));
          }, 700);
        });
      }

      return analysisPromise.then(RecommendationEngine.recommend);
    },
  };

  window.ShadeFinderProvider = ShadeFinderProvider;
  window.AnalysisService = AnalysisService;
  window.RecommendationEngine = RecommendationEngine;

  /**
   * Unlocks the Add to Cart button, which starts disabled in the markup.
   * Called either when a result comes back from the external AI tool (via
   * URL params, see applyIncomingResult) or when the in-page fallback
   * wizard's "Confirm match" button is clicked. This is the single gate:
   * nothing else in this file enables the submit button.
   */
  function unlockCart(root, result) {
    root.querySelectorAll('[data-cf-property]').forEach(function (input) {
      var key = input.getAttribute('data-cf-property');
      if (key === 'shade') input.value = result.shade;
      if (key === 'undertone') input.value = result.undertone;
      if (key === 'depth') input.value = result.depth || '';
      if (key === 'confidence') input.value = result.confidenceScore != null ? result.confidenceScore + '%' : '';
      if (key === 'analysis_id') input.value = result.analysisId || '';
      if (key === 'source') input.value = result.source || (result.isSample ? 'sample' : 'api');
    });

    var summary = root.querySelector('[data-cf-match-summary]');
    if (summary) {
      var swatch = summary.querySelector('[data-cf-swatch]');
      var shadeEl = summary.querySelector('[data-cf-result-shade]');
      var undertoneEl = summary.querySelector('[data-cf-result-undertone]');
      if (swatch) swatch.style.backgroundColor = result.hex || approximateHex(result.depth, result.undertone);
      if (shadeEl) shadeEl.textContent = result.shade;
      if (undertoneEl) undertoneEl.textContent = window.CF_STRINGS.undertoneLabel + ': ' + result.undertone;
      summary.hidden = false;
    }

    var gateHint = root.querySelector('[data-cf-gate-hint]');
    if (gateHint) gateHint.hidden = true;

    var submit = root.querySelector('[data-cf-submit]');
    root.dataset.cfUnlocked = 'true';
    if (submit && submit.dataset.soldOut !== 'true') submit.disabled = false;

    var dynamicCheckout = root.querySelector('[data-cf-dynamic-checkout]');
    if (dynamicCheckout) dynamicCheckout.hidden = false;
  }

  /**
   * Reads a shade result the customer brings back from the external AI
   * scan tool. Contract: the external tool redirects the browser to the
   * `return_to` URL it was given, appending these query params:
   *   shade, undertone (required) — plain text, e.g. "Amber Warm 4", "Warm"
   *   depth, confidence, analysis_id, source — optional
   * See ARCHITECTURE.md for the full handoff spec.
   */
  function applyIncomingResult(root) {
    var params = new URLSearchParams(window.location.search);
    var shade = params.get('shade');
    var undertone = params.get('undertone');
    if (!shade || !undertone) return;

    unlockCart(root, {
      shade: shade,
      undertone: undertone,
      depth: params.get('depth'),
      confidenceScore: params.get('confidence') ? params.get('confidence').replace('%', '') : null,
      analysisId: params.get('analysis_id'),
      hex: params.get('hex'),
      source: params.get('source') || 'scan',
    });

    // Drop the result params from the visible URL so it doesn't look messy
    // and a page refresh doesn't re-process the same query string oddly.
    params.delete('shade');
    params.delete('undertone');
    params.delete('depth');
    params.delete('confidence');
    params.delete('analysis_id');
    params.delete('hex');
    params.delete('source');
    var cleanQuery = params.toString();
    var cleanUrl = window.location.pathname + (cleanQuery ? '?' + cleanQuery : '') + window.location.hash;
    window.history.replaceState(null, '', cleanUrl);
  }

  function init(root) {
    var apiUrl = root.getAttribute('data-api-url');
    var state = { photo: null, quiz: {}, result: null };

    applyIncomingResult(root);

    var stepIndicators = root.querySelectorAll('[data-cf-step-indicator]');
    var panels = root.querySelectorAll('[data-cf-step]');

    function showStep(step) {
      panels.forEach(function (panel) {
        panel.hidden = panel.getAttribute('data-cf-step') !== String(step);
      });
      stepIndicators.forEach(function (indicator) {
        indicator.classList.toggle('is-active', indicator.getAttribute('data-cf-step-indicator') === String(step));
      });
    }

    root.querySelectorAll('[data-cf-back-to]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        showStep(btn.getAttribute('data-cf-back-to'));
      });
    });

    // --- "I know my shade": toggle + submit ---
    var codeToggles = root.querySelectorAll('[data-cf-code-toggle]');
    var codePanel = root.querySelector('[data-cf-code-panel]');
    var codeInput = root.querySelector('[data-cf-code-input]');
    var codeError = root.querySelector('[data-cf-code-error]');
    var codeSubmit = root.querySelector('[data-cf-code-submit]');

    codeToggles.forEach(function (toggle) {
      toggle.addEventListener('click', function () {
        if (!codePanel) return;
        var next = codePanel.hidden;
        codePanel.hidden = !next;
        codeToggles.forEach(function (t) { t.setAttribute('aria-expanded', next ? 'true' : 'false'); });
        if (next && codeInput) codeInput.focus();
      });
    });

    /**
     * Puts the product page back to its starting state after an item has been
     * added to the cart: shade card cleared, "I know my shade" box closed and
     * emptied, and Add to Cart switched off again until a new shade is chosen.
     */
    function resetWizard() {
      state.photo = null;
      state.result = null;

      root.querySelectorAll('[data-cf-property]').forEach(function (input) { input.value = ''; });

      var summary = root.querySelector('[data-cf-match-summary]');
      if (summary) {
        summary.hidden = true;
        var swatch = summary.querySelector('[data-cf-swatch]');
        if (swatch) swatch.style.backgroundColor = '';
        var shadeEl = summary.querySelector('[data-cf-result-shade]');
        var undertoneEl = summary.querySelector('[data-cf-result-undertone]');
        if (shadeEl) shadeEl.textContent = '';
        if (undertoneEl) undertoneEl.textContent = '';
      }

      var gateHint = root.querySelector('[data-cf-gate-hint]');
      if (gateHint) gateHint.hidden = false;

      delete root.dataset.cfUnlocked;
      var submit = root.querySelector('[data-cf-submit]');
      if (submit) submit.disabled = true;
      var dynamicCheckout = root.querySelector('[data-cf-dynamic-checkout]');
      if (dynamicCheckout) dynamicCheckout.hidden = true;

      if (codePanel) codePanel.hidden = true;
      codeToggles.forEach(function (t) { t.setAttribute('aria-expanded', 'false'); });
      if (codeInput) codeInput.value = '';
      if (codeError) codeError.hidden = true;

      var selfieInput = root.querySelector('[data-cf-selfie-input]');
      var selfiePreview = root.querySelector('[data-cf-selfie-preview]');
      if (selfieInput) selfieInput.value = '';
      if (selfiePreview) { selfiePreview.hidden = true; selfiePreview.removeAttribute('src'); }
      if (panels.length) showStep(1);
    }

    document.addEventListener('cart:item-added', function (event) {
      var form = event.detail && event.detail.form;
      if (form && root.contains(form)) resetWizard();
    });

    if (codeSubmit) {
      codeSubmit.addEventListener('click', function () {
        var match = lookupShadeCode(codeInput ? codeInput.value : '');
        if (!match) {
          if (codeError) {
            codeError.textContent = window.CF_STRINGS.idNotFound;
            codeError.hidden = false;
          }
          return;
        }
        if (codeError) codeError.hidden = true;
        unlockCart(root, {
          shade: match.shade,
          undertone: match.undertone,
          depth: match.depth,
          confidenceScore: null,
          analysisId: null,
          hex: null,
          source: 'code',
        });
      });
    }

    if (codeInput) {
      codeInput.addEventListener('keydown', function (event) {
        if (event.key === 'Enter') {
          event.preventDefault();
          if (codeSubmit) codeSubmit.click();
        }
      });
    }

    // --- Step 1: tabs ---
    var tabs = root.querySelectorAll('[data-cf-tab]');
    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        tabs.forEach(function (t) {
          t.classList.toggle('is-active', t === tab);
          t.setAttribute('aria-selected', t === tab ? 'true' : 'false');
        });
        root.querySelectorAll('[data-cf-tab-panel]').forEach(function (panel) {
          panel.hidden = panel.getAttribute('data-cf-tab-panel') !== tab.getAttribute('data-cf-tab');
        });
      });
    });

    // --- Step 1: file upload ---
    var fileInput = root.querySelector('[data-cf-selfie-input]');
    var preview = root.querySelector('[data-cf-selfie-preview]');
    if (fileInput) {
      fileInput.addEventListener('change', function () {
        var file = fileInput.files && fileInput.files[0];
        state.photo = file || null;
        if (file && preview) {
          preview.src = URL.createObjectURL(file);
          preview.hidden = false;
        }
        // A photo was chosen via the "Scan Now" button — run the analysis
        // immediately rather than waiting for a second click. Sends to
        // settings.shade_finder_api_url (AnalysisService.analyzeRemote) when
        // set; falls back to the local sample table otherwise.
        if (file) runAnalysis();
      });
    }

    // --- Step 1: quiz ---
    root.querySelectorAll('[data-cf-quiz-field]').forEach(function (group) {
      var field = group.getAttribute('data-cf-quiz-field');
      group.querySelectorAll('input[type="radio"]').forEach(function (input) {
        input.addEventListener('change', function () {
          if (input.checked) state.quiz[field] = input.value;
        });
      });
    });

    // --- Step 1 -> 2: analyze ---
    var errorEl = root.querySelector('[data-cf-error]');
    var analyzeBtn = root.querySelector('[data-cf-analyze]');

    function runAnalysis() {
      var hasPhoto = !!state.photo;
      var hasQuiz = state.quiz.undertone && state.quiz.depth;
      if (!hasPhoto && !hasQuiz) {
        if (errorEl) {
          errorEl.textContent = window.CF_STRINGS.needInput;
          errorEl.hidden = false;
        }
        return;
      }
      if (errorEl) errorEl.hidden = true;

      showStep(2);
      var analyzingEl = root.querySelector('[data-cf-analyzing]');
      var resultEl = root.querySelector('[data-cf-result]');
      if (analyzingEl) analyzingEl.hidden = false;
      if (resultEl) resultEl.hidden = true;

      ShadeFinderProvider.analyze({ apiUrl: apiUrl, photo: state.photo, quiz: state.quiz }).then(function (result) {
        state.result = result;
        renderResult(result);
        if (analyzingEl) analyzingEl.hidden = true;
        if (resultEl) resultEl.hidden = false;
      });
    }

    if (analyzeBtn) {
      analyzeBtn.addEventListener('click', runAnalysis);
    }

    function renderResult(result) {
      var swatch = root.querySelector('[data-cf-swatch]');
      var shadeEl = root.querySelector('[data-cf-result-shade]');
      var undertoneEl = root.querySelector('[data-cf-result-undertone]');
      var confidenceEl = root.querySelector('[data-cf-result-confidence]');
      var sampleBadge = root.querySelector('[data-cf-sample-badge]');

      if (swatch) swatch.style.backgroundColor = result.hex;
      if (shadeEl) shadeEl.textContent = result.shade;
      if (undertoneEl) undertoneEl.textContent = window.CF_STRINGS.undertoneLabel + ': ' + result.undertone;
      if (confidenceEl) confidenceEl.textContent = window.CF_STRINGS.confidenceLabel + ': ' + result.confidenceScore + '%';
      if (sampleBadge) sampleBadge.hidden = !result.isSample;
    }

    // --- Fallback wizard: unlock the (always-visible) Add to Cart button ---
    root.querySelectorAll('[data-cf-unlock]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (!state.result) return;
        unlockCart(root, {
          shade: state.result.shade,
          undertone: state.result.undertone,
          depth: state.result.depth,
          confidenceScore: state.result.confidenceScore,
          analysisId: state.result.analysisId,
          hex: state.result.hex,
          source: state.result.isSample ? 'sample' : 'api',
        });
        showStep(1);
      });
    });
  }

  document.querySelectorAll('[data-cf-wizard]').forEach(init);
})();
